import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { mockAdminLogin } from '../utils/mockApi';

interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface AdminContextType {
  isAuthenticated: boolean;
  adminUser: AdminUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

interface AdminProviderProps {
  children: ReactNode;
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Use mock API in development/testing
      if (import.meta.env.DEV || import.meta.env.MODE === 'test') {
        const data = await mockAdminLogin(email, password);
        setAdminUser(data.admin);
        setIsAuthenticated(true);
        return;
      }

      const response = await fetch('/api/email/admin/login', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setAdminUser(data.admin);
        setIsAuthenticated(true);
      } else {
        throw new Error(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Admin login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await fetch('/api/email/admin/logout', {
        method: 'POST',
        credentials: 'include'
      });
      
      setIsAuthenticated(false);
      setAdminUser(null);
      
      // Redirect to admin login page
      window.location.href = '/admin/login';
    } catch (error) {
      console.error('Admin logout error:', error);
      // Still clear local state even if request fails
      setIsAuthenticated(false);
      setAdminUser(null);
      window.location.href = '/admin/login';
    }
  };

  // Check authentication status
  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/api/admin/dashboard', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // We need to get admin user info separately since dashboard might not return it
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          setAdminUser(null);
        }
      } else {
        setIsAuthenticated(false);
        setAdminUser(null);
      }
    } catch (error) {
      console.error('Admin auth check error:', error);
      setIsAuthenticated(false);
      setAdminUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Check authentication status on mount
  useEffect(() => {
    // Only check auth if we're on an admin route
    if (window.location.pathname.startsWith('/admin')) {
      checkAuthStatus();
    } else {
      setIsLoading(false);
    }
  }, []);

  const value: AdminContextType = {
    isAuthenticated,
    adminUser,
    isLoading,
    login,
    logout,
    checkAuthStatus
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = (): AdminContextType => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};